import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PaginatedUrlsResponse, Pagination } from '../../shared/models/pagination.model';
import { UrlPattern } from '../models/url-pattern.model';
import { UrlCheck } from '../models/url-check.model';
import { Sitemap } from '../models/sitemap.model';


@Injectable({
  providedIn: 'root',
})
export class UrlService {

  private readonly apiUrl = environment.apiGatewayUrl; // Fetching the API URL from the environment configuration

  constructor(private readonly http: HttpClient) { }

  // POST /api/v1/starturls/{contextId}
  startUrls(contextId: string, patterns: { pattern: string; patternType: 'INCLUDE' | 'EXCLUDE' }[]): Observable<void> {
    const url = `${this.apiUrl}/webcontent/api/v1/starturls/${contextId}`;
    return this.http.post<void>(url, { patterns });
  }

  // GET /api/v1/starturls/{contextId}
  getStartUrls(contextId: string): Observable<UrlPattern> {
    const url = `${this.apiUrl}/webcontent/api/v1/starturls/${contextId}`;
    return this.http.get<UrlPattern>(url);
  }

  // GET /api/v1/starturls/paginated/{contextId}
  getStartUrlsPaginated(contextId: string, pagination: Pagination) {
    let params = new HttpParams()
      .set('pageNumber', (pagination.page - 1).toString()) // Backend page is 0-based
      .set('pageSize', pagination.pageSize.toString());

    return this.http.get<PaginatedUrlsResponse<UrlPattern>>(`${environment.apiGatewayUrl}/webcontent/api/v1/starturls/paginated/${contextId}`, { params })
      .pipe(
        map(response => ({
          ...response, // Spread the existing response properties
          patterns: response.patterns.map(pattern => {
            let reason: string | undefined;

            if (pattern.status === 'DELETING' || pattern.status === 'PENDING') {
              reason = "L'operazione può impiegare fino a 24 ore";
            } else if (!pattern.verified) {
              reason = 'Sito web da verificare';
            }

            return {
              ...pattern,
              reason
            };
          })
        }))
      );
  }

  // DELETE /webcontent/api/v1/starturls/{contextId}/{id}
  deleteStartUrlById(contextId: string, id: string): Observable<void> {
    const url = `${this.apiUrl}/webcontent/api/v1/starturls/${contextId}/${id}`;
    return this.http.delete<void>(url);
  }

  // POST /webcontent/api/v1/starturls/refreshurl
  refreshUrls(contextId: string, urls: string[]): Observable<void> {
    const url = `${this.apiUrl}/webcontent/api/v1/starturls/${contextId}/refreshurl`;
    const body = urls.map((url) => ({ url }));
    return this.http.post<void>(url, body);
  }

  // POST /webcontent/api/v1/starturls/{contextId}/refreshverification
  refreshVerification(contextId: string) {
    const url = `${this.apiUrl}/webcontent/api/v1/starturls/${contextId}/refresh-verification`;
    return this.http.post(url, undefined);
  }

  // POST /webcontent/api/v1/siteverification/create-token
  createVerificationToken(identifierUrl: string) {
    const url = `${this.apiUrl}/webcontent/api/v1/siteverification/create-token`;
    const body = {
      verificationMethod: 'FILE',
      site: {
        identifier: identifierUrl,
        type: 'SITE'
      }
    };
    return this.http.post<{ method: string, token: string }>(url, body);
  }

  // POST /webcontent/api/v1/siteverification/insert-token
  insertVerificationToken(identifierUrl: string) {
    const url = `${this.apiUrl}/webcontent/api/v1/siteverification/insert-token`;
    const body = {
      verificationMethod: 'FILE',
      site: {
        identifier: identifierUrl,
        type: 'SITE'
      }
    };
    return this.http.post<{ method: string, token: string }>(url, body);
  }

  // POST /webcontent/api/v1/starturls/{contextId}/check
  checkUrlsByContext(contextId: string, urls: string[]) {
    const url = `${this.apiUrl}/webcontent/api/v1/starturls/${contextId}/check`;
    const body = { urls };
    return this.http.post<UrlCheck[]>(url, body);
  }

  // POST /webcontent/api/v1/starturls/{contextId}/recrawl
  recrawlUrls(contextId: string, urls: string[]) {
    const url = `${this.apiUrl}/webcontent/api/v1/starturls/${contextId}/recrawl`;
    const body = { urls };
    return this.http.post(url, body);
  }

  // GET /webcontent/api/v1/sitemaps/{contextId}
  getSitemaps(contextId: string): Observable<Sitemap[]> {
    return this.http.get<Sitemap[]>(`${this.apiUrl}/webcontent/api/v1/sitemaps/${contextId}`,);
  }

  // POST /webcontent/api/v1/sitemaps/{contextId}
  addSitemaps(contextId: string, sitemaps: string[]): Observable<Sitemap[]> {
    return this.http.post<Sitemap[]>(`${this.apiUrl}/webcontent/api/v1/sitemaps/${contextId}`, sitemaps);
  }

  // DELETE /webcontent/api/v1/sitemaps/{contextId}/{siteMapId}
  deleteSitemap(contextId: string, siteMapId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/webcontent/api/v1/sitemaps/${contextId}/${siteMapId}`);
  }
}
